import React from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { Patient, ReduxState, MessageType, AttachmentType, utils } from 'kb-shared';
import Avatar from './Avatar';
import { ReactComponent as PaperclipIcon } from '../../assets/icons/paperclip.svg';
import { Storage } from 'aws-amplify';
import fetch from 'node-fetch';
import { showErrorToast } from 'utilities/notificationUtils';

const { nameToInitials } = utils;

interface Props {
  message: MessageType;
  patient: Patient;
}

const downloadFile = async (name: string, fileName: string) => {
  return Storage.vault
    .get(`documents/personal/${name || fileName}`)
    .then(results => {
      return fetch(results.toString());
    })
    .then(res => (res.status === 200 ? res.blob() : { size: 0 }))
    .then(blob => {
      if (blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        a.remove();
      } else {
        showErrorToast('File not found');
      }
    })
    .catch(err => console.log(err));
};

const Message = ({ message, patient }: Props) => {
  const { fromPatient, createdAt, senderPhotoUrl, senderName } = message;
  const recent = moment(createdAt).isSameOrAfter(moment().subtract(24, 'hours'));
  const formattedTimestamp = recent
    ? moment(message.createdAt).calendar()
    : moment(message.createdAt).format('D MMM YY');
  const { profileImage, firstName, lastName } = patient;
  const initials = fromPatient ? `${firstName[0]}${lastName[0]}` : nameToInitials(senderName);

  return (
    <Container>
      <Main fromPatient={fromPatient}>
        <MessageBubble fromPatient={fromPatient}>
          <div>{message.body}</div>
          <AttachmentWrapper>
            {message.attachments.map((attachment: AttachmentType) => (
              <AttachmentLink key={attachment.id} fromPatient={fromPatient}>
                <PaperclipIcon />
                {!fromPatient && (
                  <Link target="_blank" href={attachment.file}>
                    {attachment.fileName}
                  </Link>
                )}
                {fromPatient && attachment.uuid && (
                  <Link onClick={() => downloadFile(attachment.uuid, attachment.fileName)}>
                    {attachment.fileName}
                  </Link>
                )}
                {fromPatient && !attachment.uuid && (
                  <Link href={attachment.file} target="_blank">
                    {attachment.fileName}
                  </Link>
                )}
              </AttachmentLink>
            ))}
          </AttachmentWrapper>
        </MessageBubble>
        <Avatar
          src={fromPatient ? profileImage : senderPhotoUrl}
          initials={initials}
          style={{ margin: 5, alignSelf: 'flex-end' }}
        />
      </Main>
      <Timestamp fromPatient={fromPatient}>{formattedTimestamp}</Timestamp>
    </Container>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  patient: state.patient.patient
});

export default connect(mapStateToProps)(Message);

interface FromPatientProps {
  fromPatient: boolean;
}

const Container = styled.div`
  margin: 1.5em;
  @media (min-width: ${({ theme }) => theme.breakpoints.largePhonePortrait}) {
    margin: 2em;
  }
`;

const AttachmentWrapper = styled.div`
  display: flex;
`;

const AttachmentLink = styled.span<FromPatientProps>`
  margin-top: 10px;
  path {
    fill: ${({ fromPatient }) => (fromPatient ? 'white' : 'black')};
  }
  pointer: cursor;
  font-size: 10pt;
  display: flex;
  margin-right: 10px;
`;

const Link = styled.a`
  margin: 0 5px;
  text-decoration: underline;
  font-weight: bold;
`;

const Main = styled.div<FromPatientProps>`
  display: flex;
  flex-direction: ${({ fromPatient }) => (fromPatient ? 'row' : 'row-reverse')};
`;

const MessageBubble = styled.li<FromPatientProps>`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  background-color: ${({ theme, fromPatient }) => (fromPatient ? theme.colors.night : 'white')};
  color: ${({ theme, fromPatient }) => (fromPatient ? 'white' : theme.colors.night)};
  padding: 1em;
  border-radius: ${({ fromPatient }) => (fromPatient ? '15px 15px 0 15px' : '15px 15px 15px 0')};
  width: -webkit-fill-available;
  white-space: pre-wrap;
`;

const Timestamp = styled.div<FromPatientProps>`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  font-size: 0.75em;
  text-align: ${({ fromPatient }) => (fromPatient ? 'left' : 'right')};
  margin-top: 5px;
`;
